<template>
    <div class="view-report-filter-panel sticky-filter-panel">
        <div class="view-report-filter-panel--content flex-grow-1 ms-4 pe-3">

            <div class="accordion" id="view-report-filter-panel-accordion">
                <template v-for="(group, index) in dimensional_filters_list" :key="index">
                    <div v-if="group && group.filter_group" class="accordion-item">
                        <h2 class="accordion-header">
                            <button class="accordion-button px-0" 
                                    :class="{ 'collapsed': active_tab_index !== index, 'disabled': !groupAppliesToSection(index), 'pe-none': !groupAppliesToSection(index) }" 
                                    type="button"
                                    :aria-expanded="groupAppliesToSection(index) && active_tab_index === index" 
                                    :aria-controls="'collapse-' + index" 
                                    :aria-disabled="!groupAppliesToSection(index)"
                                    :title="!groupAppliesToSection(index) ? 'Not applicable to this tab' : ''"
                                    @click="groupAppliesToSection(index) && setActiveTab(index)">
                                <span v-if="groupAppliesToSection(index)">{{ group.filter_group }}</span>
                                <span v-else class="text-muted">{{ group.filter_group }} <small class="ms-1">(Not applicable to this tab)</small></span>
                            </button>
                        </h2>
                        <div :id="'collapse-' + index"
                             class="accordion-collapse collapse"
                             :class="{ 'show': groupAppliesToSection(index) && active_tab_index === index }">
                            <div class="accordion-body p-0">
                                <template v-for="(filter, filter_index) in group.filters" :key="`${index}-${filter_index}-${filter.id}`">
                                    <div v-if="(filter && filter.options && Array.isArray(filter.options) && filter.options.length) || filter.search_key != ''" 
                                         class="mb-2" 
                                         :class="{ 'opacity-50': !groupAppliesToSection(index) }"
                                         :data-filter-id="filter.id"
                                         :title="!groupAppliesToSection(index) ? 'This filter does not apply to the current tab' : ''">
                                        <advanced-filter-dropdown 
                                            :key="`filter-${index}-${filter_index}-${filter.id}`"
                                            :filter="filter" 
                                            :group_index="index" 
                                            :field_index="filter_index" 
                                            :active_group_index="active_tab_index"
                                            :if_benchmark_report="if_benchmark_report"
                                            :if_allow_delete="groupAppliesToSection(index)"
                                            @updateFilter="updateFilterInGroup" 
                                            @deleteFilter="deleteFilterInGroup"
                                            @searchInLongOptionList="searchInLongOptionList">
                                        </advanced-filter-dropdown>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
        </div>

        <div class="sticky-bottom-container">
            <div class="sticky-bottom-content">
                <div class="d-flex flex-row justify-content-between align-items-center flex-nowrap gap-2">
                    <div class="d-flex flex-row justify-content-end align-items-center gap-2">
                        <span>{{ filter_count }} {{(filter_count > 1 ? 'Filters': 'Filter')}} {{filter_count_label}}</span>
                    </div>

                    <div class="d-flex flex-row justify-content-end align-items-center flex-nowrap gap-2">
                        <button type="button" class="btn btn-secondary btn-icon-only-md" @click.prevent="addFilters($event)" :disabled="if_refreshing" data-bs-toggle="tooltip" title="Add New Filter">
                            <img src="/img/icons/avid-icons/filter-funnel-add.svg" alt="Add New Filter" class="icon">
                        </button>                        
                        <button type="button" class="btn btn-secondary" @click.prevent="clearFilters()">
                            Reset
                        </button>
                        <button v-if="!if_refreshing" type="button" class="btn btn-primary" :disabled="!checkIfDropdownsLoaded()" @click.prevent="applyFilter()">
                            Apply
                        </button>
                        <button v-else type="button" class="btn btn-primary" disabled>
                            <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"><span class="visually-hidden"></span></div>
                            Loading
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Swal from 'sweetalert2';
    import vSelect from "vue3-select";
    import { router } from '@inertiajs/vue3';
    import { defineComponent } from 'vue';
    import { useNotifications } from '../../composables/useNotifications';

    export default defineComponent({
        components: {
            Swal, vSelect
        },
        setup() {
            const { showNotification } = useNotifications();
            return {
                showNotification
            };
        },
        props: {
            page: {
                type: Object,
                required: true
            },
            applied_chart_filter: {
                type: Object,
                required: true
            },
            section_id: {
                type: [Number, String],
                required: false,
                default: null
            },
            if_benchmark_report: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                local_page: {},
                if_sticky: false,
                dimensional_filters_list: [],  // format: array of objects {filter_group: '', is_tab_active: boolean, filters: []}
                local_applied_chart_filter: null,
                applied_filters_list:[],
                filter_errors: {},
                if_refreshing: false,
                filter_count: 0,
                filter_count_label: 'Applied',
                active_tab_index: 0,
                benchmark_defaults_set: false,
                is_loading_filter_options: false,
                is_initial_load: true
            };
        },
        beforeMount() {
            this.local_page = {...this.page};
            this.initializeFilters();
        },
        mounted() {
            this.getAppliedFilterWithValue();
            this.loadPresetFilters();
            this.loadFilterOptions();
            // Mark initial load as complete after a short delay to allow watchers to settle
            this.$nextTick(() => {
                setTimeout(() => {
                    this.is_initial_load = false;
                }, 100);
            });
        },
        watch: {
            page: {
                handler(newVal) {
                    if (!newVal) return;
                    
                    // Prevent recursive updates by checking if the values are actually different
                    if (JSON.stringify(this.local_page) === JSON.stringify(newVal)) {
                        return;
                    }

                    this.local_page = {...this.page};

                    // If dimensional_filters_list hasn't been set up yet (e.g., async page load), build it now
                    if (!this.dimensional_filters_list || this.dimensional_filters_list.length === 0) {
                        this.initializeFilters();
                    } else {
                        this.updateFilterGroups();
                    }
                    
                    // Use nextTick to ensure the DOM has updated before loading filter options
                    // Only load filter options if this is not the initial load (mounted() already handles that)
                    if (!this.is_initial_load) {
                        this.$nextTick(() => {
                            let needsLoad = false;
                            this.dimensional_filters_list.forEach((filter_group, group_index) => {
                                if (!filter_group || !filter_group.filters) return;
                                filter_group.filters.forEach((filter, filter_index) => {
                                    if (!filter || !filter.options || filter.options.length === 0) {
                                        needsLoad = true;
                                    }
                                });
                            });
                            if (needsLoad && !this.is_loading_filter_options) {
                                this.loadFilterOptions();
                            }
                        });
                    }

                    // Use nextTick to ensure all filters are loaded before getting applied values
                    this.$nextTick(() => {
                        this.getAppliedFilterWithValue();
                    });
                },
                deep: true
            },
            applied_chart_filter: {
                handler(newVal) {
                    if (!newVal) return;
                    this.local_applied_chart_filter = this.applied_chart_filter;
                    this.updateURL();
                },
                deep: true
            },
            if_benchmark_report(newVal, oldVal) {
                // When switching into benchmark mode (or benchmark data finally arrives), pull latest page data and reload filters.
                // Only trigger if actually changing to benchmark mode (not on initial mount when already true)
                if (newVal && newVal !== oldVal) {
                    this.local_page = { ...this.page };
                    // Re-initialise filters only if dimensions are present (prevents duplicate work)
                    if (this.local_page && this.local_page.dimensions && this.local_page.dimensions.length) {
                        this.initializeFilters();
                        this.loadPresetFilters();
                        // Only load filter options if not already loading
                        if (!this.is_loading_filter_options) {
                            this.loadFilterOptions();
                        }
                    }
                }
            },
            is_loading_filter_options: {
                handler(newVal) {
                    this.$emit('filterLoading', newVal);
                },
                immediate: true
            },
            section_id(newVal, oldVal) {
                if (oldVal == null) return;
                if (newVal !== oldVal) {
                    if (this.active_tab_index >= 0 && this.dimensional_filters_list[this.active_tab_index] && !this.groupAppliesToSection(this.active_tab_index)) {
                        this.active_tab_index = this.getFirstApplicableGroupIndex();
                    }
                    this.updateDisabledStateForSection();
                }
            }
        },
        methods: {
            groupAppliesToSection(groupIndex) {
                const group = this.dimensional_filters_list[groupIndex];
                if (!group || group.filter_group == null) return true;
                const sectionIds = group.section_ids;
                if (sectionIds == null || !Array.isArray(sectionIds) || sectionIds.length === 0) return true;
                const sid = parseInt(this.section_id, 10);
                if (Number.isNaN(sid)) return false;
                return sectionIds.some(id => Number(id) === sid);
            },
            getFirstApplicableGroupIndex() {
                for (let i = 0; i < this.dimensional_filters_list.length; i++) {
                    if (this.groupAppliesToSection(i)) return i;
                }
                return -1;
            },
            updateDisabledStateForSection() {
                if (!this.dimensional_filters_list) return;
                this.dimensional_filters_list.forEach((group, groupIndex) => {
                    if (group && group.filters) {
                        const applies = this.groupAppliesToSection(groupIndex);
                        group.filters.forEach(f => { f.disabled = !applies; });
                    }
                });
                if (this.local_page && this.local_page.dimensions) {
                    this.local_page.dimensions.forEach(dim => {
                        const group = this.dimensional_filters_list.find(g => g && g.filter_group === dim.filter_group);
                        if (group) {
                            const groupIndex = this.dimensional_filters_list.indexOf(group);
                            dim.disabled = !this.groupAppliesToSection(groupIndex);
                        }
                    });
                }
                this.$forceUpdate();
            },
            initializeFilters() {
                if (!this.local_page.dimensions) return;
                
                for (let i = 0; i < this.local_page.dimensions.length; i++) {
                    this.local_page.dimensions[i].disabled = true;
                    this.local_page.dimensions[i].options = [];
                    this.local_page.dimensions[i].db_search = false;
                }
                this.local_applied_chart_filter = this.applied_chart_filter;
                this.loadFilterGroups();
                this.active_tab_index = this.getFirstApplicableGroupIndex();
                this.updateDisabledStateForSection();
                this.getFilterCount();
            },
            dateRangeDisplay(value) {
                if (!value) return '';
                if (value.start_date && value.end_date) {
                    return this.formatUtcDate(value.start_date) + ' - ' + this.formatUtcDate(value.end_date);
                } else if (value.start_date) {
                    return 'On or After ' + this.formatUtcDate(value.start_date);
                } else if (value.end_date) {
                    return 'Prior to ' + this.formatUtcDate(value.end_date);
                }
                return '';
            },
            monthRangeDisplay(value) {
                if (!value) return '';
                if (value.start_date && value.end_date) {
                    return this.formatUtcMonth(value.start_date) + ' - ' + this.formatUtcMonth(value.end_date);
                } else if (value.start_date) {
                    return 'On or After ' + this.formatUtcMonth(value.start_date);
                } else if (value.end_date) {
                    return 'Prior to ' + this.formatUtcMonth(value.end_date);
                }
                return '';
            },
            formatUtcDate(dateString) {
                if (!dateString) return '';
                const date = new Date(dateString);
                const offset = date.getTimezoneOffset();
                date.setMinutes(date.getMinutes() + offset);
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                const yyyy = date.getFullYear();
                return `${mm}/${dd}/${yyyy}`;
            },
            formatUtcMonth(dateString) {
                if (!dateString) return '';
                const date = new Date(dateString);
                const offset = date.getTimezoneOffset();
                date.setMinutes(date.getMinutes() + offset);
                const month = date.toLocaleString('default', { month: 'short' });
                const year = date.getFullYear();
                return `${month} ${year}`;
            },
            getAppliedFilterWithValue() {
                if (!this.dimensional_filters_list) return;
                
                const newAppliedFilters = [];
                this.dimensional_filters_list.forEach((group, group_i) => {
                    if (!group || !group.filters) return;
                    
                    group.filters.forEach((filter, filter_i) => {
                        if (!filter) return;

                        if (!filter.value && (filter.field_type === 'multi_select' || filter.field_type === 'month_picker' || filter.field_type === 'array_single_select' || filter.field_type === 'array_multi_select')) {
                            filter.value = [];
                        }
                        if (!filter.value && filter.field_type === 'single_select') {
                            filter.value = '';
                        }
                        if (filter.value && (filter.value.length > 0 ||
                            ((filter.field_type === 'date_range' || filter.field_type === 'month_range') && filter.value && (filter.value.end_date !== null || filter.value.start_date !== null)))) {

                            if (filter.field_type === "month_range") {
                                filter.value.start_date = filter.value.start_date ? moment(String(filter.value.start_date)).utc().format('YYYY-MM-DD') : null;
                                filter.value.end_date = filter.value.end_date ? moment(String(filter.value.end_date)).utc().format('YYYY-MM-DD') : null;
                            }

                            newAppliedFilters.push({
                                filter_id: filter.id,
                                filter_label: filter.label,
                                filter_value: filter.value,
                                filter_default_value: ((typeof filter.column === 'string' || filter.column instanceof String) && filter.column.indexOf('time_period.year_type') !== -1 ? (this.page?.client?.default_reporting_period ?? filter.default_value) : filter.default_value),
                                filter_column_name: filter.column,
                                filter_type: filter.field_type,
                                is_required: filter.is_required,
                                group_index: group_i,
                                filter_index_in_group: filter_i
                            });
                        }
                    });
                });

                // Only update if there are actual changes
                if (JSON.stringify(this.applied_filters_list) !== JSON.stringify(newAppliedFilters)) {
                    this.applied_filters_list = newAppliedFilters;
                    this.getFilterCount();
                }
            },
            // removeAppliedFilter(filter, filter_index) {
            //     if (!filter) return;
                
            //     if (filter.filter_id === -1 || (this.applied_chart_filter.if_chart_filter_applied && filter.filter_value.includes(this.applied_chart_filter.chart_filters[0].value))) {
            //         this.local_applied_chart_filter = {
            //             if_chart_filter_applied: false,
            //         };
            //     }
                
            //     this.applied_filters_list.splice(filter_index, 1);
            //     const currentFilter = this.dimensional_filters_list[filter.group_index].filters[filter.filter_index_in_group];
                
            //     if (!currentFilter) return;
                
            //     if (currentFilter.field_type === 'single_select') {
            //         currentFilter.value = '';
            //     } else if (currentFilter.field_type === 'multi_select' || currentFilter.field_type === 'month_picker') {
            //         currentFilter.value = [];
            //     } else if (currentFilter.field_type === 'numeric_slider') {
            //         currentFilter.value = null;
            //     } else if (currentFilter.field_type === 'date_range' || currentFilter.field_type === 'month_range') {
            //         currentFilter.value = {
            //             start_date: null,
            //             end_date: null
            //         };
            //     }

            //     this.getFilterCount();
            // },
            handleScroll() {
                if(this.$refs.stickyDiv == null) return;

                const divTop = this.$refs.stickyDiv.getBoundingClientRect().top;
                this.if_sticky = divTop <= 0;
            },
            loadFilterGroups(){
                var group_array = [];

                var group_name = null;
                var group_filters_temp = [];
                this.page.dimensions.forEach(pf => {
                    // Deep clone so v-model changes in the panel don't mutate the parent's page.dimensions until Apply is clicked
                    const clonedPf = JSON.parse(JSON.stringify(pf));

                    if (pf.is_required){
                        this.filter_errors[pf.label] = '';
                    }

                    if(group_name && pf.filter_group != group_name){
                        var section_ids = this.getSectionIdsForGroup(group_name, group_filters_temp);
                        group_array.push({
                            filter_group: group_name,
                            section_ids: section_ids,
                            filters: group_filters_temp
                        });
                        group_name = pf.filter_group;
                        group_filters_temp = [clonedPf];
                    } else {
                        group_name = pf.filter_group;
                        group_filters_temp.push(clonedPf);
                    }
                });
                var section_ids = this.getSectionIdsForGroup(group_name, group_filters_temp);
                group_array.push({
                    filter_group: group_name,
                    section_ids: section_ids,
                    filters: group_filters_temp
                });

                // Respect backend order: sort by dimensional_filter_groups.order
                const groupsMeta = this.page.dimensional_filter_groups || this.page.dimensionalFilterGroups || [];
                group_array.sort((a, b) => {
                    const ga = groupsMeta.find(dg => dg.name === a.filter_group);
                    const gb = groupsMeta.find(dg => dg.name === b.filter_group);
                    const orderA = ga && ga.order != null ? Number(ga.order) : 999;
                    const orderB = gb && gb.order != null ? Number(gb.order) : 999;
                    return orderA - orderB;
                });
                this.dimensional_filters_list = group_array;
            },
            getSectionIdsForGroup(group_name, group_filters_temp) {
                const pageGroups = this.page.dimensional_filter_groups || this.page.dimensionalFilterGroups || [];
                const g = pageGroups.find(dg => dg.name === group_name);
                if (g && g.section_ids && Array.isArray(g.section_ids) && g.section_ids.length) return g.section_ids;
                const firstDim = group_filters_temp && group_filters_temp[0];
                const grp = firstDim && firstDim.report_page_dimensional_filter_group;
                if (grp && grp.section_ids && Array.isArray(grp.section_ids) && grp.section_ids.length) return grp.section_ids;
                return null;
            },
            updateFilterGroups(){
                this.page.dimensions.forEach(pf => {
                    // Hide filter if it's multi_select, has only 1 option, and that option is the default value
                    if ((pf.field_type === 'multi_select' || pf.field_type === 'array_single_select' || pf.field_type === 'array_multi_select') &&
                        pf.options && Array.isArray(pf.options) && pf.options.length === 1 &&
                        pf.default_value && Array.isArray(pf.default_value) && pf.default_value.length === 1 &&
                        pf.options[0] === pf.default_value[0]) {
                        // Remove this filter from dimensional_filters_list if it exists
                        this.dimensional_filters_list.forEach(group => {
                            if (group.filters) {
                                const filterIndex = group.filters.findIndex(filter => filter.id === pf.id);
                                if (filterIndex >= 0) {
                                    group.filters.splice(filterIndex, 1);
                                }
                            }
                        });
                        return; // Skip processing this filter
                    }

                    var group_index = this.dimensional_filters_list.findIndex(group => group.filter_group == pf.filter_group);
                    if (group_index>=0){
                        var filter_index =this.dimensional_filters_list[group_index].filters.findIndex(filter => filter.id == pf.id);
                        if(filter_index >= 0){
                            this.dimensional_filters_list[group_index].filters[filter_index].value = pf.value;
                        } else {
                            // New filter in existing group — clone to avoid mutating parent
                            this.dimensional_filters_list[group_index].filters.push(JSON.parse(JSON.stringify(pf)));
                        }
                    } else if (pf.id >= 0 && pf.filter_group) {
                        // New page-level dimension — clone to avoid mutating parent
                        this.dimensional_filters_list.push({
                            filter_group: pf.filter_group,
                            filters: [JSON.parse(JSON.stringify(pf))]
                        });
                    } else {
                        // Chart filter (negative id) — clone to avoid mutating parent
                        this.dimensional_filters_list.push({
                            filter_group: null,
                            filters: [JSON.parse(JSON.stringify(pf))]
                        });
                    }
                });

                if (!this.applied_chart_filter.if_chart_filter_applied){
                    // when removing chart filter, need to remove the chart filter and filter group in this.dimensional_filters_list as well
                    this.dimensional_filters_list = this.dimensional_filters_list.filter(group => group.filter_group != null);
                }
            },

            addAcceptedFilter(dim) {
                if (!dim || !dim.id) return;

                // Add to local_page dimensions if not already there
                if (!this.local_page.dimensions) {
                    this.local_page.dimensions = [];
                }
                if (!this.local_page.dimensions.some(d => d.id === dim.id)) {
                    this.local_page.dimensions.push(dim);
                }

                // Prepare the filter entry with loading state
                const filterEntry = { ...dim, disabled: true, options: [], db_search: false };

                // Add to the correct group in dimensional_filters_list
                const groupIndex = this.dimensional_filters_list.findIndex(g => g.filter_group === dim.filter_group);
                let targetGroupIndex;
                if (groupIndex >= 0) {
                    if (!this.dimensional_filters_list[groupIndex].filters.some(f => f.id === dim.id)) {
                        this.dimensional_filters_list[groupIndex].filters.push(filterEntry);
                    }
                    targetGroupIndex = groupIndex;
                } else {
                    this.dimensional_filters_list.push({
                        filter_group: dim.filter_group,
                        filters: [filterEntry]
                    });
                    targetGroupIndex = this.dimensional_filters_list.length - 1;
                }

                // Expand the group that contains the new filter
                this.active_tab_index = targetGroupIndex;

                // Load options for the new filter so it becomes visible
                if (dim.field_type === 'numeric_slider') {
                    this.getMinMax(filterEntry);
                } else {
                    const payload = {
                        page: JSON.parse(JSON.stringify(this.local_page)),
                        filters: [filterEntry],
                    };
                    if (payload.page.sections) {
                        payload.page.sections.forEach(section => {
                            if (section.charts) {
                                section.charts.forEach(chart => {
                                    if (chart && chart.chart) {
                                        chart.chart.data = null;
                                    }
                                });
                            }
                        });
                    }

                    window.axios.post('/api/metric_filter/options', payload)
                        .then(response => {
                            const filters = response.data.filters || [];
                            const match = filters.find(f => f.id === dim.id);
                            if (match && match.options) {
                                // Find the filter entry in dimensional_filters_list and update it
                                for (let i = 0; i < this.dimensional_filters_list.length; i++) {
                                    const fIdx = this.dimensional_filters_list[i].filters.findIndex(f => f.id === dim.id);
                                    if (fIdx >= 0) {
                                        this.dimensional_filters_list[i].filters[fIdx].options = match.options;
                                        this.dimensional_filters_list[i].filters[fIdx].disabled = !this.groupAppliesToSection(i);
                                        this.dimensional_filters_list[i].filters[fIdx].db_search = match.options.length >= 999;
                                        if (match.counts) {
                                            this.dimensional_filters_list[i].filters[fIdx].counts = match.counts;
                                        }
                                        // Trigger reactivity
                                        this.dimensional_filters_list[i].filters[fIdx] = JSON.parse(
                                            JSON.stringify(this.dimensional_filters_list[i].filters[fIdx])
                                        );
                                        break;
                                    }
                                }
                            }
                        });
                }

                this.getAppliedFilterWithValue();
            },
            loadPresetFilters() {
                this.dimensional_filters_list.forEach((filter_group, group_index) => {
                    filter_group.filters.forEach((filter, filter_index) => {
                        //If the filter doesn't have a selected value...
                        if (((filter.field_type == 'date_range' || filter.field_type == 'month_range') && filter.value &&
                              (filter.value.start_date || filter.value.end_date)) ||
                            (filter.field_type != 'date_range' && filter.field_type != 'month_range' && filter.value && filter.value.length != 0)) {
                                var data = {
                                    client: this.page.client,
                                    source: 'data_blend',
                                    column: filter.column,
                                    limit: 1000,
                                    data_source_id: this.page.data_blend.id,
                                    staging_table: false
                                };

                                window.axios.post('/api/bigquery/get_samples', data)
                                .then(response => {
                                    var db_values = [];

                                    //Remove the null value
                                    for(var i = response.data.samples.length - 1; i >= 0; i--){
                                        if(response.data.samples[i].name == null)
                                            response.data.samples.splice(i, 1);
                                        else {
                                            if (filter.field_type == 'single_select' || filter.field_type == 'multi_select'
                                            || filter.field_type == 'array_single_select' || filter.field_type == 'array_multi_select' || filter.field_type == 'month_picker'|| filter.field_type == 'date_range'|| filter.field_type == 'month_range' ){
                                                db_values.push(response.data.samples[i].name);
                                            }
                                        }
                                    }

                                    if (filter.field_type == 'month_picker') {
                                        // sort by month order
                                        const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                        db_values.sort((a, b) => monthOrder.indexOf(a) > monthOrder.indexOf(b) ? 1 : -1);
                                    } else {
                                        db_values.sort((a, b) => (a.name > b.name ) ? 1 : -1);
                                    }

                                    this.dimensional_filters_list[group_index].filters[filter_index].options = db_values;
                                    this.dimensional_filters_list[group_index].filters[filter_index].disabled = !this.groupAppliesToSection(group_index);
                                    this.dimensional_filters_list[group_index].filters[filter_index] = JSON.parse(JSON.stringify(this.dimensional_filters_list[group_index].filters[filter_index]));

                                });
                        }

                    });
                });
            },
            getMinMax(filter) {
                var data = {
                  source: 'data_blend',
                  data_source_id: this.page.data_blend.id,
                  client: this.page.client,
                  column: filter.column,
                };

                window.axios.post('/api/bigquery/get_min_max', data)
                    .then(response => {
                        if (typeof response.data === 'object' && response.data.min !== undefined && response.data.max !== undefined) {
                            // Direct min/max values from API
                            filter.slider_min = response.data.min;
                            filter.slider_max = response.data.max;
                        } else if (Array.isArray(response.data)) {
                            // Array of values to process
                            let value = response.data.reduce((accumulator, current) => {
                                accumulator.push(parseInt(current.name));
                                return accumulator;
                            }, []);
                            filter.slider_min = Math.min(...value);
                            filter.slider_max = Math.max(...value);
                            filter.options = [];
                            filter.options.push(filter.slider_min);
                            filter.options.push(filter.slider_max);
                        }

                        if(filter.options && filter.options.length == 2 && !isNaN(filter.options[0]) && !isNaN(filter.options[1]) ){
                            if(filter.options[0] < filter.slider_min)
                                filter.slider_min = filter.options[0];
                            if(filter.options[1] > filter.slider_max)
                                filter.slider_max = filter.options[1];
                        }
                        for (var i=0; i< this.dimensional_filters_list.length; i++){
                            for (var j=0; j<this.dimensional_filters_list[i].filters.length; j++){
                                if (this.dimensional_filters_list[i].filters[j].id == filter.id){
                                    this.dimensional_filters_list[i].filters[j].disabled = !this.groupAppliesToSection(i);
                                    this.dimensional_filters_list[i].filters[j] = JSON.parse(JSON.stringify(filter));
                                    break;
                                }
                            }
                        }
                        this.$forceUpdate();
                    });
            },
            loadFilterOptions() {
                // Prevent multiple simultaneous calls
                if (this.is_loading_filter_options) {
                    return;
                }

                // If page information hasn't loaded yet (common in benchmark views), exit early.
                if (!this.local_page || !this.local_page.dimensions || this.local_page.dimensions.length === 0) {
                    return;
                }

                this.is_loading_filter_options = true;
                //Unused filters
                let unused_filters = [];
                this.dimensional_filters_list.forEach((filter_group, group_index) => {
                    filter_group.filters.forEach((filter, filter_index) => {
                        //If the filter doesn't have a selected value...
                        if(filter.field_type == 'numeric_slider'){
                            this.getMinMax(filter);
                        }
                        else if (!((filter.field_type == 'date_range' || filter.field_type == 'month_range') && filter.value &&
                              (filter.value.start_date || filter.value.end_date)) &&
                            !(filter.field_type != 'date_range' && filter.field_type != 'month_range' && filter.value && filter.value.length != 0)) {
                            unused_filters.push(filter);
                        }

                    });
                })

                // Determine API endpoint & payload based on report type
                let apiEndpoint = this.if_benchmark_report ? '/api/benchmarks/filters' : '/api/metric_filter/options';
                let data;
                if (this.if_benchmark_report) {
                    // Benchmarks API expects *all* dimensions, not just unused ones
                    data = {
                        page: JSON.parse(JSON.stringify(this.local_page)),
                        filters: this.local_page.dimensions,
                    };
                } else {
                    data = {
                        page: JSON.parse(JSON.stringify(this.local_page)),
                        filters: unused_filters,
                    };
                }

                // loop through data.page.sections.charts.chart and set data to NULL
                if (data.page && data.page.sections && Array.isArray(data.page.sections)) {
                    data.page.sections.forEach(section => {
                        if (section.charts && Array.isArray(section.charts)) {
                            section.charts.forEach(chart => {
                                if (chart) {
                                    chart.chart.data = null;
                                }
                            });
                        }
                    });
                }

                window.axios.post(apiEndpoint, data)
                    .then(response => {
                        this.is_loading_filter_options = false;
                        // For both report types, the response structure contains a `filters` array.
                        let filters = response.data.filters;

                        // Iterate through local dimensional filters list and update matching filters with options
                        for (var i=0; i< this.dimensional_filters_list.length; i++){
                            for (var j=0; j<this.dimensional_filters_list[i].filters.length; j++){
                                for(var k = 0; k < filters.length; k++){
                                    if (this.dimensional_filters_list[i].filters[j].id == filters[k].id){
                                        //Resort the month dropdown option
                                        if (this.dimensional_filters_list[i].filters[j].field_type == 'month_picker' && filters[k].options != null ) {
                                            // sort by month order
                                            const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                            filters[k].options.sort((a, b) => monthOrder.indexOf(a) > monthOrder.indexOf(b) ? 1 : -1);
                                        }
                                        if (filters[k].options) {
                                            const colName = this.dimensional_filters_list[i].filters[j].column;
                                            const shouldPreserve = this.if_benchmark_report && ['org_vertical','org_annual_revenue'].includes(colName);
                                            if (!shouldPreserve || !this.dimensional_filters_list[i].filters[j].options || this.dimensional_filters_list[i].filters[j].options.length === 0) {
                                                this.dimensional_filters_list[i].filters[j].options = filters[k].options;
                                            } else if (shouldPreserve) {
                                                // Merge new options (if any counts changed) but keep full list
                                                const existingOpts = new Set(this.dimensional_filters_list[i].filters[j].options);
                                                filters[k].options.forEach(opt => existingOpts.add(opt));
                                                this.dimensional_filters_list[i].filters[j].options = Array.from(existingOpts);
                                            }

                                            // Merge counts
                                            const existingCounts = this.dimensional_filters_list[i].filters[j].counts || {};
                                            const newCounts = filters[k].counts || {};
                                            this.dimensional_filters_list[i].filters[j].counts = { ...existingCounts, ...newCounts };
                                        }

                                        // Additional benchmark counts handling
                                        if (this.if_benchmark_report && filters[k].counts) {
                                            const colName = this.dimensional_filters_list[i].filters[j].column;
                                            const shouldPreserve = ['org_vertical','org_annual_revenue'].includes(colName);
                                            if (shouldPreserve) {
                                                const existingCounts = this.dimensional_filters_list[i].filters[j].counts || {};
                                                this.dimensional_filters_list[i].filters[j].counts = { ...existingCounts, ...filters[k].counts };
                                            } else {
                                                this.dimensional_filters_list[i].filters[j].counts = filters[k].counts;
                                            }
                                        }

                                        // For the benchmark report, make sure Period Type always defaults to 'Trailing 12 Months' and ALL options remain visible
                                        if (this.if_benchmark_report && this.dimensional_filters_list[i].filters[j].column === 'period_type') {
                                            // Default value
                                            if (!this.dimensional_filters_list[i].filters[j].value || this.dimensional_filters_list[i].filters[j].value === '') {
                                                this.dimensional_filters_list[i].filters[j].value = 'Trailing 12 Months';
                                            }
                                            // Ensure options contain the default value plus any others (backend sometimes returns just the selected one)
                                            const defaultOptions = ['Trailing 12 Months', 'Calendar Year']; // Extend if more options exist
                                            defaultOptions.forEach(opt => {
                                                if (!this.dimensional_filters_list[i].filters[j].options.includes(opt)) {
                                                    this.dimensional_filters_list[i].filters[j].options.push(opt);
                                                }
                                            });
                                        }

                                        // (Removed inline default vertical/revenue logic; handled after loop)

                                        this.dimensional_filters_list[i].filters[j].disabled = !this.groupAppliesToSection(i);
                                        this.dimensional_filters_list[i].filters[j].db_search = (filters[k].options != null && filters[k].options.length >= 999); //999 since it removes the null

                                        this.dimensional_filters_list[i] = JSON.parse(JSON.stringify(this.dimensional_filters_list[i]));
                                    }
                                }
                            }
                        }

                        // Remove filters that should be hidden (multi_select with 1 option matching default)
                        this.removeHiddenFilters();
                        
                        // Recalculate filter count after removing hidden filters
                        this.getFilterCount();

                        this.$forceUpdate();

                        // After all filters processed, set benchmark defaults once
                        if (this.if_benchmark_report && !this.benchmark_defaults_set) {
                            this.setBenchmarkDefaultSelections();
                            this.benchmark_defaults_set = true;
                            // Update applied filters list with new defaults
                            this.getAppliedFilterWithValue();
                        }
                //Catch the error if it is a 403 or 404 error
                }).catch(error => {
                    // this.is_loading_filter_options = false;
                    if(error.response && error.response.status == 403){
                        Swal.fire({
                            title: 'Error',
                            text: 'You do not have permission to view this page. It is likely due to a lost session. Please refresh the browser.',
                            icon: 'error',
                            confirmButtonText: "OK",
                            customClass: {
                                popup: 'swal-error'
                            },
                        });
                    }
                    if(error.response && error.response.status == 404){
                        Swal.fire({
                            title: 'Error',
                            text: 'Something went wrong when trying to load the filters. Please refresh the page and try again.',
                            icon: 'error',
                            confirmButtonText: "OK",
                            customClass: {
                                popup: 'swal-error'
                            },
                        });
                    }
                });
            },
            removeHiddenFilters() {
                // Remove filters that are multi_select, have only 1 option, and that option is the default value
                // Also remove empty groups after filtering
                this.dimensional_filters_list = this.dimensional_filters_list.map((group, groupIndex) => {
                    if (!group.filters) return group;
                    
                    // Filter out hidden filters
                    const filteredFilters = group.filters.filter(filter => {
                        // Get default_value from filter or from original page dimensions
                        let defaultValue = filter.default_value;
                        if (!defaultValue && this.page && this.page.dimensions) {
                            const originalFilter = this.page.dimensions.find(d => d.id === filter.id);
                            if (originalFilter) {
                                defaultValue = originalFilter.default_value;
                            }
                        }
                        
                        // Check if filter should be hidden
                        if ((filter.field_type === 'multi_select' || filter.field_type === 'array_single_select' || filter.field_type === 'array_multi_select') &&
                            filter.options && Array.isArray(filter.options) && filter.options.length === 1 &&
                            defaultValue && Array.isArray(defaultValue) && defaultValue.length === 1 &&
                            filter.options[0] === defaultValue[0]) {
                            return false; // Remove this filter
                        }
                        return true; // Keep this filter
                    });
                    
                    // Return group with filtered filters
                    return {
                        ...group,
                        filters: filteredFilters
                    };
                }).filter(group => group.filters && group.filters.length > 0); // Remove empty groups
            },
            checkIfDropdownsLoaded(){
                for (var i=0; i< this.dimensional_filters_list.length; i++){
                    for (var j=0; j<this.dimensional_filters_list[i].filters.length; j++){
                         if (this.dimensional_filters_list[i].filters[j].disabled == true)
                            return false;
                    }
                }
                return true;
            },
            setActiveTab(index) {
                this.active_tab_index = this.active_tab_index === index ? -1 : index;
            },
            getTileClass(filter, option){
                var classes = "";
                if (filter.field_type == 'tile-single-select'){
                    if (filter.value[0] && filter.value[0].id == option.id){
                        classes = 'tile-selected'
                    }
                }
                return classes;
            },
            selectOption(group_index, filter_index, option){
                if (this.dimensional_filters_list[group_index].filters[filter_index].field_type == 'tile-single-select'){
                    this.dimensional_filters_list[group_index].filters[filter_index].value = [option];
                } else { //'tile-multi-select'
                    this.dimensional_filters_list[group_index].filters[filter_index].value.push(option);
                }
                this.filter_count_label = 'Selected';
                this.getFilterCount();
            },
            updateFilterInGroup(filter_details){
                if (!this.groupAppliesToSection(filter_details.group_index)) return;
                // if there is chart filter applied, change filter option selection will remove the chart filter
                //Test if the filter value is an array
                if(Array.isArray(filter_details.filter_value)){
                    if(this.applied_chart_filter.if_chart_filter_applied && filter_details.filter_value.includes(this.applied_chart_filter.chart_filters[0].value)){
                        this.local_applied_chart_filter = {
                            if_chart_filter_applied: false,
                        };
                    }
                }
                this.dimensional_filters_list[filter_details.group_index].filters[filter_details.field_index].value = filter_details.filter_value;
                this.filter_count_label = 'Selected';
                this.getAppliedFilterWithValue();
                this.getFilterCount();
            },
            filterHasSelectedValue(filter) {
                if (!filter || !filter.value) return false;
                if (filter.field_type === 'date_range' || filter.field_type === 'month_range') {
                    return !!(filter.value.start_date || filter.value.end_date);
                }
                if (Array.isArray(filter.value)) {
                    return filter.value.length > 0;
                }
                return filter.value !== '' && filter.value != null;
            },
            deleteFilterInGroup(filter_details){
                if (!this.groupAppliesToSection(filter_details.group_index)) return;
                // Get the filter to delete
                const filter = this.dimensional_filters_list[filter_details.group_index].filters[filter_details.field_index];
                
                if (!filter) {
                    return;
                }
                
                const self = this;
                
                // Show confirmation dialog
                Swal.fire({
                    title: 'Remove Filter?',
                    html: `Are you sure you want to remove <strong>${filter.label}</strong> from this report?<br><br>Once removed, this filter will be removed for all users on this report.`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, remove it',
                    cancelButtonText: 'Cancel',
                    confirmButtonColor: '#F81820',
                    cancelButtonColor: '#6c757d',
                    reverseButtons: true,
                    customClass: {
                        popup: 'swal-warning'
                    }
                }).then((result) => {
                    if (result.isConfirmed) {
                        // Call the API to delete the filter
                        const filterId = filter.id;
                        const hadSelectedValue = self.filterHasSelectedValue(filter);
                        window.axios.delete(`/api/insights/page/${self.local_page.id}/dimensional_filter/${filterId}`)
                            .then(response => {
                                // Remove the filter from dimensional_filters_list
                                self.dimensional_filters_list[filter_details.group_index].filters.splice(filter_details.field_index, 1);
                                
                                // If the group is now empty, remove it
                                if (self.dimensional_filters_list[filter_details.group_index].filters.length === 0) {
                                    self.dimensional_filters_list.splice(filter_details.group_index, 1);
                                }
                                
                                // Update filter counts
                                self.getAppliedFilterWithValue();
                                self.getFilterCount();
                                
                                // Force Vue to re-render
                                self.$forceUpdate();
                                
                                // If the removed filter had a selected value, refetch report data
                                if (hadSelectedValue) {
                                    self.applyFilter();
                                }
                                
                                // Show success message
                                self.showNotification({
                                    text: `<strong>${filter.label}</strong> has been removed from this report.`,
                                    type: 'success'
                                });
                            })
                            .catch(error => {
                                console.error('Error deleting filter:', error);
                                Swal.fire({
                                    title: 'Error',
                                    text: 'Something went wrong when trying to remove the filter. Please try again.',
                                    icon: 'error',
                                    confirmButtonText: 'OK',
                                    customClass: {
                                        popup: 'swal-error'
                                    }
                                });
                            });
                    }
                });
            },
            searchInLongOptionList(search_details){
                var filter = this.dimensional_filters_list[search_details.group_index].filters[search_details.field_index];
                filter.disabled = true;
                var data = {
                    client: this.page.client,
                    source: 'data_blend',
                    column: filter.column,
                    limit: 1000,
                    data_source_id: this.page.data_blend.id,
                    staging_table: false,
                    search: search_details.search_key
                };
                window.axios.post('/api/bigquery/get_samples', data)
                .then(response => {
                    var db_values = [];

                    //Remove the null value
                    for(var i = response.data.samples.length - 1; i >= 0; i--){
                        if(response.data.samples[i].name == null)
                            response.data.samples.splice(i, 1);
                        else {
                            if (filter.field_type == 'single_select' || filter.field_type == 'multi_select' || filter.field_type == 'array_single_select' || filter.field_type == 'array_multi_select'){
                                db_values.push(response.data.samples[i].name);
                            }
                        }
                    }
                    db_values.sort((a, b) => (a.name > b.name ) ? 1 : -1);

                    // Update the filter object directly - Vue 3's reactivity system will handle this
                    this.dimensional_filters_list[search_details.group_index].filters[search_details.field_index] = {
                        ...filter,
                        options: db_values,
                        disabled: false,
                        search_key: search_details.search_key
                    };
                    
                    this.$forceUpdate();
                });
            },
            applyFilter(){
                // loop through this.dimensional_filters_list and destruct it into a flat array
                var self = this;
                this.if_refreshing = true;

                this.$emit('refreshing', this.if_refreshing);

                // Benchmark privacy validation (minimum 6 orgs)
                if (this.if_benchmark_report && !this.validateBenchmarkFilters()) {
                    this.if_refreshing = false;
                    this.$emit('refreshing', this.if_refreshing);
                    return;
                }

                var dimension_list = [];
                this.dimensional_filters_list.forEach((group)=>{
                    dimension_list = dimension_list.concat(group.filters)
                });
                // Include hidden dimensions from original page (e.g. Transaction Type with 1 option) to preserve their values
                const visibleIds = new Set(dimension_list.map(d => d.id));
                (this.page.dimensions || []).forEach((origDim) => {
                    if (origDim.id >= 0 && !visibleIds.has(origDim.id) && origDim.value != null) {
                        const hasVal = (origDim.field_type === 'date_range' || origDim.field_type === 'month_range')
                            ? (origDim.value.start_date || origDim.value.end_date)
                            : (Array.isArray(origDim.value) ? origDim.value.length > 0 : origDim.value !== '');
                        if (hasVal) {
                            dimension_list.push({ ...origDim });
                            visibleIds.add(origDim.id);
                        }
                    }
                });

                dimension_list.forEach((dimension, i)=>{
                    if (dimension.field_type == "month_range"){
                        dimension_list[i].value.start_date = dimension_list[i].value.start_date ? moment(String(dimension_list[i].value.start_date)).utc().format('YYYY-MM-DD') : null;
                        dimension_list[i].value.end_date = dimension_list[i].value.end_date ? moment(String(dimension_list[i].value.end_date)).utc().format('YYYY-MM-DD') : null;
                    }

                    //If it is the transaction date and the setting is different than the default value, save it to a cookie
                    if((dimension.column == 'transactions.time_period.year_type' || dimension.column == 'FORMAT_DATE("%b",transactions.date)') && dimension.value != null){
                        // Convert column name to lowercase and hash it
                        let text = dimension.column.toLowerCase();
                        text = text.replace(/(\r\n|\n|\r)/g, " ");
                        text = text.replace(/\s\s+/g, " ");
                        let cookieName = 'filter_' + this.page.client_id + '_' + text;
                        let cookieValue = JSON.stringify({column: dimension.column, value: dimension.value});
                        let expirationDate = new Date();
                        expirationDate.setTime(expirationDate.getTime() + (24 * 60 * 60 * 1000)); // 24 hours
                        document.cookie = `${cookieName}=${cookieValue};expires=${expirationDate.toUTCString()};path=/`;
                    }
                })

                this.local_page.dimensions = dimension_list;
                this.getFilterCount();

                // Is_Donation filter field is hidden, if it is included in the original dimension list, we need to add it in when applying filter

                // this.loadFilterOptions();

                var page_copy = JSON.parse(JSON.stringify(this.local_page));
                delete page_copy.client;
                if (page_copy.sections) {
                    page_copy.sections.forEach(section => {
                        if (section.charts) {
                            section.charts.forEach(chart => {
                                if (chart.chart) {
                                    delete chart.chart.data;
                                    delete chart.chart.labels;
                                    delete chart.chart.metric_formats;
                                }
                            });
                        }
                    });
                }

                window.axios.post('/api/insights/page/' + this.local_page.id, page_copy)
                    .then(async (response) => {
                        self.filter_count_label = 'Applied';

                        var page_temp = self.local_page;
                        self.local_page = response.data.page;
                        self.local_page.dimensions = page_temp.dimensions;
                        self.if_refreshing = false;

                        self.$emit('refreshing', self.if_refreshing);
                        if (self.local_applied_chart_filter.if_chart_filter_applied == false) {
                            self.$emit('removeChartFilter', null);
                        }
                        self.$emit('updatePage', self.local_page);

                        // Update URL with params so refresh preserves filter selection (router.visit ensures server receives them)
                        const basePath = '/insights/' + self.page.client.url + '/' + self.page.url + '/' + self.section_id;
                        const filterObj = await self.buildFilterObjFromDimensions(page_temp.dimensions);
                        if (Object.keys(filterObj).length > 0) {
                            const paramsStr = JSON.stringify(filterObj);
                            const url = paramsStr.length >= 1600
                                ? await self.saveParamsAndGetUrl(paramsStr, basePath)
                                : basePath + '?params=' + encodeURIComponent(paramsStr);
                            window.history.replaceState({}, '', url);
                            // router.visit(url, { preserveScroll: true });
                        }
                    }).catch(error=> {
                        // Ensure refreshing state is cleared on error
                        self.if_refreshing = false;
                        self.$emit('refreshing', self.if_refreshing);
                        
                        //Sweet alert telling them there was an error and that they should refresh
                        Swal.fire({
                            title: 'Error',
                            text: 'Something went wrong when trying to apply the filters. Please refresh the page and try again.',
                            icon: 'error',
                            confirmButtonText: "Refresh",
                            customClass: {
                                popup: 'swal-error'
                            },
                        }).then((result) => {
                            if (result.isConfirmed) {
                                location.reload();
                            }
                        });
                    });
            },
            validateBenchmarkFilters() {
                // Returns true if filters are acceptable, false if too specific (<6 orgs)
                // Only enforce for Vertical and Annual Revenue filters
                const verticalColumns = ['org_vertical'];
                const revenueColumns = ['org_annual_revenue'];

                let isValid = true;

                const checkFilter = (filter) => {
                    if (!filter || !Array.isArray(filter.value) || !filter.counts) return true; // cannot validate

                    // if user narrowed selection (value length != options length)
                    if (filter.value.length !== filter.options.length) {
                        // compute org_count
                        let org_count = 0;
                        filter.value.forEach(opt => {
                            if (filter.counts[opt] !== undefined) {
                                org_count += filter.counts[opt];
                            }
                        });
                        if (org_count < 6) return false;
                    }
                    return true;
                };

                // iterate through filters
                this.dimensional_filters_list.forEach(group => {
                    group.filters.forEach(filter => {
                        if (verticalColumns.includes(filter.column)) {
                            isValid = isValid && checkFilter(filter);
                        }
                        if (revenueColumns.includes(filter.column)) {
                            isValid = isValid && checkFilter(filter);
                        }
                    });
                });

                if (!isValid) {
                    Swal.fire({
                        title: 'Your filter is too specific',
                        text: 'Your current filter selection would match fewer than 6 organizations, which is not allowed for privacy reasons. Please broaden your filter to see results.',
                        icon: 'warning',
                        showCancelButton: false,
                        customClass: {
                            popup: 'swal-warning'
                        },
                    });
                }

                return isValid;
            },
            arraysAreEqual(a, b) {
                if (a === b) return true;

                if (typeof a != 'object' || a === null || typeof b != 'object' || b === null) {
                    return false;
                }

                let keysA = Object.keys(a);
                let keysB = Object.keys(b);

                if (keysA.length !== keysB.length) return false;

                keysA.sort();
                keysB.sort();

                for (let i = 0; i < keysA.length; i++) {
                    if (keysA[i] !== keysB[i]) return false;
                    if (!this.arraysAreEqual(a[keysA[i]], b[keysB[i]])) return false;
                }

                return true;
            },

            async buildFilterObjFromDimensions(dimensions) {
                var filter_obj = {};
                if (!dimensions || !dimensions.length) return filter_obj;

                for (const dim of dimensions) {
                    const defaultValue = (typeof dim.column === 'string' || dim.column instanceof String) && dim.column.indexOf('time_period.year_type') !== -1
                        ? this.page.client.default_reporting_period : dim.default_value;

                    const hasValue = (dim.field_type === 'date_range' || dim.field_type === 'month_range')
                        ? (dim.value && (dim.value.start_date || dim.value.end_date))
                        : (dim.value && (Array.isArray(dim.value) ? dim.value.length > 0 : dim.value !== '' && dim.value != null));

                    if (!hasValue) continue;
                    if (defaultValue != null && this.arraysAreEqual(defaultValue, dim.value)) continue;

                    let colName = (dim.column || '').toString().toLowerCase()
                        .replace(/(\r\n|\n|\r)/g, ' ')
                        .replace(/\s\s+/g, ' ');
                    if (!colName) continue;

                    const hashedColumnName = await this.sha256(colName);
                    filter_obj[hashedColumnName] = dim.value;
                }
                return filter_obj;
            },
            async saveParamsAndGetUrl(paramsStr, basePath) {
                const response = await window.axios.post('/api/insights/parameters', {
                    client_id: this.page.client_id,
                    params: paramsStr
                });
                return basePath + '?param_id=' + response.data.id;
            },
            async updateURL(){
                // update url with filters
                const currentUrl = document.location.pathname;
                var filter_obj = {};

                // Handle each filter asynchronously
                for (const applied_filter of this.applied_filters_list) {
                    if(applied_filter.filter_default_value == null ||
                        (typeof applied_filter.filter_default_value === "object" ||
                            Array.isArray(applied_filter.filter_default_value))
                            && !this.arraysAreEqual(applied_filter.filter_default_value, applied_filter.filter_value) ||
                        (typeof applied_filter.filter_default_value !== "object" && Array.isArray(applied_filter.filter_default_value) == false) && applied_filter.filter_default_value != applied_filter.filter_value
                        ){

                        let col_name = applied_filter.filter_column_name;
                        if(typeof col_name === 'string' || col_name instanceof String){
                            col_name = col_name.toLowerCase();
                            //Replace new lines with a space
                            col_name = col_name.replace(/(\r\n|\n|\r)/gm, " ");
                            //Replace multiple spaces with a single space
                            col_name = col_name.replace(/\s\s+/g, " ");
                        }

                        const hashedColumnName = await this.sha256(col_name);
                        filter_obj[hashedColumnName] = applied_filter.filter_value;
                    }
                }

                //Now loop through the non-applied filter list
                //If it has a default value but it isn't in the applied filter list, then add it to the filter_obj
                for (const group of this.dimensional_filters_list) {
                    for (const filter of group.filters) {

                        //Find it the filter.column is not in the applied_filters_list in the filter_column_name property
                        var found = false;
                        for(var i = 0; i < this.applied_filters_list.length; i++){
                            if(this.applied_filters_list[i].filter_column_name == filter.column){
                                found = true;
                                break;
                            }
                        }

                        //If it wasn't in the applied filters list and it had a default value
                        if(!found &&
                            ((filter.field_type != 'date_range' && filter.field_type != 'month_range' && filter.default_value && filter.value.length != 0)
                            || ((filter.field_type == 'date_range' || filter.field_type == 'month_range') && filter.default_value && (filter.default_value.start_date || filter.default_value.end_date)))) {

                            let col_name = filter.column;
                            if(typeof col_name === 'string' || col_name instanceof String){
                                col_name = col_name.toLowerCase();
                                //Replace new lines with a space
                                col_name = col_name.replace(/(\r\n|\n|\r)/gm, " ");
                                //Replace multiple spaces with a single space
                                col_name = col_name.replace(/\s\s+/g, ' ');
                            }
                            const hashedColumnName = await this.sha256(col_name);

                            //They have to have some value or it will set it to the default value. The value will be an empty value
                            if(filter.field_type == 'date_range' || filter.field_type == 'month_range' )
                                filter_obj[hashedColumnName] = {
                                    start_date: null,
                                    end_date: null
                                };
                            else if(filter.field_type == 'single_select')
                                filter_obj[hashedColumnName] = "";
                            else if(filter.field_type == 'multi_select' || filter.field_type == 'month_picker' || filter.field_type == 'array_single_select' || filter.field_type == 'array_multi_select') {
                                filter_obj[hashedColumnName] = [];
                            }

                        }

                    }
                }

                //append the params to the export button
                let export_btn = document.getElementById('export-button');

                //If the filter_obj doesn't have any values, just remove the params variable
                if(Object.keys(filter_obj).length == 0){
                    window.history.replaceState({}, '', currentUrl);

                    if(export_btn)
                        export_btn.href = currentUrl.replace('insights', 'export');
                }
                else {
                    //If it is longer than 1600 characters, save it to the DB
                    let params = encodeURI(JSON.stringify(filter_obj));

                    if(params.length >= 1600){
                        //Save what section they were looking at
                        var data = {
                          client_id: this.page.client_id,
                          params: params
                        };
                        window.axios.post('/api/insights/parameters', data).then(response => {
                            var newURL = currentUrl + '?param_id=' + response.data.id;
                            window.history.replaceState({}, '', newURL);

                            if(export_btn)
                                export_btn.href = newURL.replace('insights', 'export');
                        })
                    }
                    else{
                        var newURL = currentUrl + '?params=' + params;
                        window.history.replaceState({}, '', newURL);

                        if(export_btn)
                            export_btn.href = newURL.replace('insights', 'export');
                    }
                }
            },
            async sha256(message) {
                // encode as UTF-8
                const msgBuffer = new TextEncoder().encode(message);

                // hash the message
                const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

                // convert ArrayBuffer to Array
                const hashArray = Array.from(new Uint8Array(hashBuffer));

                // convert bytes to hex string
                const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
                return hashHex;
            },

            addFilters(event) {
                const button = event?.currentTarget;
                const tooltip = window.bootstrap?.Tooltip?.getInstance(button);

                if (tooltip) {
                    tooltip.hide();
                }

                if (button) {
                    button.blur();
                }

                Swal.fire({
                    title: 'Add a New Filter',
                    html: `
                        <p class="mb-2">Describe the new filter option you want to add to this report. Once added, it will be available to all users.</p>
                        <textarea id="filter-description" class="form-control" placeholder="E.g., Filter by fund; Filter by appeal; Date range for gift date; etc." style="width: 100%; min-height: 100px;"></textarea>
                    `,
                    showCancelButton: true,
                    confirmButtonText: 'Start',
                    cancelButtonText: 'Cancel',
                    didOpen: () => {
                        const confirmBtn = document.querySelector('.swal2-confirm');
                        if (confirmBtn) {
                            confirmBtn.innerHTML = '<img src="/img/icons/avid-icons/edna.svg" class="icon me-1" style="width:20px;height:20px" alt=""> Start';
                        }
                        const textarea = document.getElementById('filter-description');
                        if (textarea) {
                            textarea.focus();
                        }
                    },
                    preConfirm: () => {
                        const value = document.getElementById('filter-description') && document.getElementById('filter-description').value;
                        return value ? value.trim() : null;
                    }
                }).then((result) => {
                    if (result.isConfirmed && result.value) {
                        const userDescription = result.value;
                        window.axios.post(`/api/insights/page/${this.page.id}/filter-suggestions`, {
                            user_description: userDescription
                        }).then(() => {
                            this.showNotification({
                                icon: '/img/icons/nav-icons/Edna.svg',
                                text: 'We\'re analyzing your request. You\'ll be notified when your proposed filter is ready for review!',
                                type: 'info'
                            });
                        }).catch((err) => {
                            this.showNotification({
                                text: err.response?.data?.message || 'Failed to request filter suggestion. Please try again.',
                                type: 'error'
                            });
                        });
                    }
                });
            },
            clearFilters(){
                for (var i=0; i< this.dimensional_filters_list.length; i++){
                    for (var j=0; j<this.dimensional_filters_list[i].filters.length; j++){
                         if (!this.dimensional_filters_list[i].filters[j].is_required){
                            const filter = this.dimensional_filters_list[i].filters[j];
                            
                            // For benchmark reports, reset to default values (all selected) instead of clearing
                            if (this.if_benchmark_report) {
                                if (filter.field_type == "multi_select" || filter.field_type == "month_picker" || filter.field_type == "array_single_select" || filter.field_type == "array_multi_select") {
                                    // Select all available options
                                    filter.value = filter.options && filter.options.length > 0 ? [...filter.options] : [];
                                } else if (filter.field_type == "single_select") {
                                    // Use default value if available, otherwise empty string
                                    // Special case: period_type defaults to 'Trailing 12 Months'
                                    if (filter.column === 'period_type') {
                                        filter.value = 'Trailing 12 Months';
                                    } else {
                                        filter.value = filter.default_value || '';
                                    }
                                } else if (filter.field_type == "date_range" || filter.field_type == "month_range") {
                                    // Use default value if available, otherwise null
                                    filter.value = filter.default_value || { start_date: null, end_date: null };
                                } else if (filter.field_type == "numeric_slider") {
                                    // Use default value if available, otherwise null
                                    filter.value = filter.default_value || null;
                                } else {
                                    filter.value = '';
                                }
                            } else {
                                // Original behavior for non-benchmark reports: clear filters
                                if(filter.field_type == "multi_select" || filter.field_type == "array_single_select" || filter.field_type == "array_multi_select")
                                    filter.value = [];
                                else
                                    filter.value = '';
                            }
                         }
                    }
                }
                //Remove the params
                const currentUrl = document.location.pathname;
                let export_btn = document.getElementById('export-button');
                window.history.replaceState({}, '', currentUrl);
                if(export_btn)
                    export_btn.href = currentUrl.replace('insights', 'export');

                this.getFilterCount();
                this.applyFilter();
            },
            getFilterCount(){
                var filter_count_temp = 0;

                this.dimensional_filters_list.forEach((filter_group, group_index) => {
                    filter_group.filters.forEach((filter, filter_index) => {
                        // Skip chart filters (negative IDs)
                        if (filter.id < 0) {
                            return;
                        }
                        
                        // Skip filters that should be hidden
                        if (this.shouldHideFilterForCount(filter)) {
                            return;
                        }
                        
                        if((filter.field_type == 'date_range' || filter.field_type == 'month_range') && filter.value && (filter.value.start_date || filter.value.end_date))
                            filter_count_temp++;
                        else if(filter.field_type != 'date_range' && filter.field_type != 'month_range' && filter.value && filter.value.length != 0)
                            filter_count_temp++;

                    });
                })
                this.filter_count = filter_count_temp;
            },
            shouldHideFilterForCount(filter) {
                // Hide filter if it's multi_select, has only 1 option, and that option is the default value
                if (!filter || (filter.field_type !== 'multi_select' && filter.field_type !== 'array_single_select' && filter.field_type !== 'array_multi_select')) {
                    return false;
                }
                
                // Get options and default_value from filter or original page dimensions
                let options = filter.options;
                let defaultValue = filter.default_value;
                
                // If options or default_value not available, check original page dimensions
                if (!options || !Array.isArray(options) || options.length === 0) {
                    if (this.local_page && this.local_page.dimensions) {
                        const originalDim = this.local_page.dimensions.find(d => d.id === filter.id);
                        if (originalDim && originalDim.options && Array.isArray(originalDim.options) && originalDim.options.length > 0) {
                            options = originalDim.options;
                        } else {
                            // Options not loaded yet, can't determine if should hide
                            return false;
                        }
                    } else {
                        // Options not loaded yet, can't determine if should hide
                        return false;
                    }
                }
                
                if (!defaultValue || !Array.isArray(defaultValue) || defaultValue.length === 0) {
                    if (this.local_page && this.local_page.dimensions) {
                        const originalDim = this.local_page.dimensions.find(d => d.id === filter.id);
                        if (originalDim && originalDim.default_value && Array.isArray(originalDim.default_value) && originalDim.default_value.length > 0) {
                            defaultValue = originalDim.default_value;
                        } else {
                            // Default value not available, can't determine if should hide
                            return false;
                        }
                    } else {
                        // Default value not available, can't determine if should hide
                        return false;
                    }
                }
                
                // Check if filter should be hidden
                if (options && Array.isArray(options) && options.length === 1 &&
                    defaultValue && Array.isArray(defaultValue) && defaultValue.length === 1 &&
                    options[0] === defaultValue[0]) {
                    return true;
                }
                
                return false;
            },
            checkAndApplyFilters() {
                // First, sync the changes from the parent component
                this.local_page = {...this.page};
                this.updateFilterGroups();
                
                // Check if any filters have been reset (empty values) compared to their previous state
                let hasResetFilters = false;
                let previousAppliedFilters = [...this.applied_filters_list];
                
                // Get current applied filters after syncing
                this.getAppliedFilterWithValue();
                
                // Compare with previous state to see if any filters were removed
                if (this.applied_filters_list.length < previousAppliedFilters.length) {
                    hasResetFilters = true;
                } else {
                    // Check if any specific filters were reset
                    this.dimensional_filters_list.forEach((group) => {
                        if (!group || !group.filters) return;
                        
                        group.filters.forEach((filter) => {
                            if (!filter) return;
                            
                            // Check if filter value is empty/reset
                            if (filter.field_type === 'single_select' && filter.value === '') {
                                hasResetFilters = true;
                            } else if ((filter.field_type === 'multi_select' || filter.field_type === 'month_picker' || filter.field_type === 'array_single_select' || filter.field_type === 'array_multi_select') &&
                                      (!filter.value || filter.value.length === 0)) {
                                hasResetFilters = true;
                            } else if ((filter.field_type === 'date_range' || filter.field_type === 'month_range') && 
                                      (!filter.value || (!filter.value.start_date && !filter.value.end_date))) {
                                hasResetFilters = true;
                            } else if (filter.field_type === 'numeric_slider' && filter.value === null) {
                                hasResetFilters = true;
                            }
                        });
                    });
                }
                
                // If any filters have been reset, automatically apply the filters
                if (hasResetFilters && !this.if_refreshing) {
                    this.applyFilter();
                }
            },
            navigateToFilter(filter) {
                // Find the filter in the dimensional_filters_list
                let foundGroupIndex = -1;
                let foundFilterIndex = -1;
                
                for (let groupIndex = 0; groupIndex < this.dimensional_filters_list.length; groupIndex++) {
                    const group = this.dimensional_filters_list[groupIndex];
                    if (!group || !group.filters) continue;
                    
                    for (let filterIndex = 0; filterIndex < group.filters.length; filterIndex++) {
                        const currentFilter = group.filters[filterIndex];
                        if (currentFilter && currentFilter.id === filter.filter_id) {
                            foundGroupIndex = groupIndex;
                            foundFilterIndex = filterIndex;
                            break;
                        }
                    }
                    if (foundGroupIndex !== -1) break;
                }
                
                if (foundGroupIndex !== -1 && foundFilterIndex !== -1) {
                    // Expand the accordion group
                    this.active_tab_index = foundGroupIndex;
                    
                    // Use nextTick to ensure the accordion is expanded before scrolling
                    this.$nextTick(() => {
                        // Find the filter element and scroll to it
                        const filterElement = document.querySelector(`[data-filter-id="${filter.filter_id}"]`);
                        if (filterElement) {
                            filterElement.scrollIntoView({ 
                                behavior: 'smooth', 
                                block: 'center' 
                            });
                        }
                    });
                }
            },
            // Helper to default vertical and annual revenue filters to all options on first load
            setBenchmarkDefaultSelections() {
                this.dimensional_filters_list.forEach(group => {
                    group.filters.forEach(filter => {
                        if (['org_vertical', 'org_annual_revenue'].includes(filter.column)) {
                            if (!filter.value || filter.value.length === 0) {
                                filter.value = [...(filter.options || [])];
                            }
                            if (filter.counts) {
                                let org_cnt = 0;
                                filter.value.forEach(opt => {
                                    if (filter.counts[opt] !== undefined) {
                                        org_cnt += filter.counts[opt];
                                    }
                                });
                                filter.org_count = org_cnt;
                            }
                        }
                    });
                });
            },
        }
    });
</script>
